import {
  Button,
  CardHeader,
  Combobox,
  type ComboboxChoice,
  Input,
  SegmentedControl,
  StatusPill,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Copy, Eye, EyeOff, Landmark, KeyRound, Unplug } from 'lucide-react';
import { useState } from 'react';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { PageHeader } from '../../app/page-title.js';
import { formatDateTime } from '../../lib/time-zone.js';
import {
  getApiError,
  onlinePaymentsApi,
  type OnlinePaymentAccount,
  type OnlinePaymentEnvironment,
} from '../../lib/api.js';

const KEY = ['company', 'online-payments', 'settings'] as const;

const ENVIRONMENT_LABELS: Record<OnlinePaymentEnvironment, string> = {
  SIMULATED: 'Simulado',
  ASAAS_SANDBOX: 'Teste do Asaas',
  ASAAS_PRODUCTION: 'Produção do Asaas',
};

/// Cobrança online (ADR 0060): a conta do Asaas da empresa e a de cada unidade que receba na
/// própria conta. A chave nunca volta para a tela; só os quatro últimos caracteres.
export function OnlinePaymentsSettings({
  units,
  timeZone,
}: {
  units: Array<{ id: string; displayName: string }>;
  timeZone: string;
}) {
  const query = useQuery({
    queryKey: KEY,
    queryFn: () => onlinePaymentsApi.settings(),
    retry: false,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });
  const active = (unitId: string | null) =>
    query.data?.accounts.find((account) => account.active && account.businessUnitId === unitId);
  const company = active(null);

  return (
    <section className="max-w-3xl">
      <PageHeader
        trail="Configurações / Cobrança online"
        title="Cobrança online"
        description="Conecte a conta do Asaas da empresa para gerar Pix e link de pagamento nas vendas. O dinheiro cai direto nessa conta, e o pagamento se confirma sozinho quando cai."
      />
      <p className="mb-4 text-sm text-content-muted">
        O cadastro no Asaas pertence ao estabelecimento, que recebe diretamente e paga as tarifas
        contratadas com o provedor. A Hue cobra pelo módulo conforme o plano, sem comissão por
        transação.
      </p>
      <div className="grid gap-4">
        {query.isLoading ? (
          <LoadingCard label="Carregando a cobrança online" />
        ) : query.error && !query.data ? (
          <ErrorCard message={getApiError(query.error)} />
        ) : query.data ? (
          <>
            <section
              aria-label="Conta da empresa"
              className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-4"
            >
              <CardHeader title="Conta da empresa" icon={<Building2 className="size-4" />} />
              {company ? (
                <AccountCard
                  key={company.id}
                  account={company}
                  timeZone={timeZone}
                  environments={query.data.environments}
                />
              ) : (
                <ConnectForm
                  businessUnitId={null}
                  environments={query.data.environments}
                  submitLabel="Conectar conta"
                />
              )}
            </section>
            {units.length ? (
              <UnitsSection
                units={units}
                accounts={query.data.accounts}
                environments={query.data.environments}
                timeZone={timeZone}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

/// Só as unidades com conta própria viram cartão: com 200 unidades, a tela continua do tamanho
/// das exceções, e a escolha de quem passa a usar conta própria é uma busca.
function UnitsSection({
  units,
  accounts,
  environments,
  timeZone,
}: {
  units: Array<{ id: string; displayName: string }>;
  accounts: OnlinePaymentAccount[];
  environments: OnlinePaymentEnvironment[];
  timeZone: string;
}) {
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<string>();
  const own = units.flatMap((unit) => {
    const account = accounts.find((item) => item.active && item.businessUnitId === unit.id);
    return account ? [{ unit, account }] : [];
  });
  const others = units.filter((unit) => !own.some((item) => item.unit.id === unit.id));
  const unit = others.find((item) => item.id === chosen);
  const close = () => {
    setAdding(false);
    setChosen(undefined);
  };

  return (
    <section
      aria-label="Unidades"
      className="grid gap-3 rounded-2xl border border-surface-border bg-surface-card p-4"
    >
      <CardHeader title="Unidades" icon={<Landmark className="size-4" />} />
      <p className="text-sm text-content-muted">
        Uma unidade com CNPJ próprio pode receber na própria conta. As demais usam a conta da
        empresa.
      </p>
      {own.length ? (
        <ul className="grid gap-3">
          {own.map(({ unit: item, account }) => (
            <li key={item.id} className="grid gap-3 rounded-xl border border-surface-border p-3">
              <div>
                <strong className="text-sm">{item.displayName}</strong>
                <p className="text-xs text-content-muted">Conta própria</p>
              </div>
              <AccountCard
                key={account.id}
                account={account}
                environments={environments}
                timeZone={timeZone}
                disconnectLabel="Usar a conta da empresa"
              />
            </li>
          ))}
        </ul>
      ) : null}
      {others.length ? (
        <p className="text-sm">
          {own.length
            ? `${others.length === 1 ? 'A outra unidade usa' : `As outras ${others.length} unidades usam`} a conta da empresa.`
            : `${others.length === 1 ? 'A unidade usa' : `Todas as ${others.length} unidades usam`} a conta da empresa.`}
        </p>
      ) : null}
      {adding ? (
        <div className="grid gap-3 rounded-xl border border-brand-500/50 p-3">
          <Combobox
            label="Unidade"
            placeholder="Buscar unidade"
            selectedLabel={unit?.displayName ?? ''}
            clearable={false}
            options={others.map((item) => ({ value: item.id, label: item.displayName }))}
            onSelect={(choice: ComboboxChoice) =>
              choice.kind === 'option' ? setChosen(choice.value) : undefined
            }
            emptyLabel="Nenhuma unidade com esse nome"
          />
          {unit ? (
            <ConnectForm
              key={unit.id}
              businessUnitId={unit.id}
              environments={environments}
              submitLabel={`Conectar conta de ${unit.displayName}`}
              onCancel={close}
              onConnected={close}
            />
          ) : (
            <div>
              <Button variant="outline" onClick={close}>
                Voltar
              </Button>
            </div>
          )}
        </div>
      ) : others.length ? (
        <div>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            Usar conta própria em uma unidade
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function ConnectForm({
  businessUnitId,
  environments,
  submitLabel,
  onCancel,
  onConnected,
  initialEnvironment,
}: {
  businessUnitId: string | null;
  environments: OnlinePaymentEnvironment[];
  submitLabel: string;
  onCancel?: () => void;
  onConnected?: () => void;
  initialEnvironment?: OnlinePaymentEnvironment;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [environment, setEnvironment] = useState<OnlinePaymentEnvironment>(
    initialEnvironment ??
      (environments.includes('ASAAS_SANDBOX')
        ? 'ASAAS_SANDBOX'
        : (environments[0] ?? 'ASAAS_SANDBOX')),
  );
  const [step, setStep] = useState<1 | 2>(initialEnvironment ? 2 : 1);
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function connect() {
    if (environment !== 'SIMULATED' && apiKey.trim().length < 20)
      return setError(
        'Cole a chave de API inteira, como o Asaas mostra em Integrações → Chave de API.',
      );
    setSaving(true);
    setError(undefined);
    try {
      await onlinePaymentsApi.connect({
        businessUnitId,
        environment,
        ...(environment === 'SIMULATED' ? {} : { apiKey: apiKey.trim() }),
      });
      await client.invalidateQueries({ queryKey: ['company', 'online-payments'] });
      snackbar.success('Conta de cobrança conectada.');
      onConnected?.();
    } catch (value) {
      setError(getApiError(value));
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-3">
      <h3 className="text-sm font-semibold">
        {step === 1 ? '1. Escolher ambiente' : '2. Validar e conectar'}
      </h3>
      {step === 1 ? (
        <>
          <p className="text-sm text-content-muted">
            Teste sem dinheiro real ou conecte diretamente a produção. Cada ambiente tem cadastro,
            chave e webhook próprios.
          </p>
          {environments.length > 1 ? (
            <SegmentedControl
              label="Ambiente"
              value={environment}
              onChange={(value) => {
                setEnvironment(value);
                setApiKey('');
                setError(undefined);
              }}
              options={environments.map((value) => ({ value, label: ENVIRONMENT_LABELS[value] }))}
            />
          ) : null}
          <div>
            <Button onClick={() => setStep(2)}>Continuar</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold">{ENVIRONMENT_LABELS[environment]}</p>
          {environment === 'SIMULATED' ? (
            <p className="text-xs text-content-muted">
              Só no desenvolvimento: nenhuma conta, nenhum dinheiro. Os botões da cobrança simulam o
              que a cliente faria.
            </p>
          ) : (
            <>
              <p className="text-sm text-content-muted">
                {environment === 'ASAAS_SANDBOX'
                  ? 'Crie um cadastro separado em sandbox.asaas.com para testar sem movimentar dinheiro real.'
                  : 'Use o cadastro de produção do estabelecimento, regularizado conforme os requisitos do Asaas. A chave de teste não funciona aqui.'}{' '}
                Gere a chave em Integrações → Chaves de API. A permissão de saques não é necessária
                para gerar cobranças.
              </p>
              <SecretInput
                label="Chave de API do Asaas"
                value={apiKey}
                onChange={setApiKey}
                hint={
                  environment === 'ASAAS_SANDBOX'
                    ? 'Gere no ambiente de teste (sandbox.asaas.com), em Integrações → Chave de API. É gratuito.'
                    : 'Gere na conta do Asaas, em Integrações → Chave de API.'
                }
              />
              <p className="text-xs text-content-muted">
                A chave fica guardada cifrada e nunca é mostrada de novo; só os quatro últimos
                caracteres aparecem.
              </p>
            </>
          )}
          {error ? <RequestAlert message={error} /> : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void connect()} disabled={saving}>
              {saving ? 'Conectando…' : submitLabel}
            </Button>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => {
                setStep(1);
                setApiKey('');
                setError(undefined);
              }}
            >
              Escolher outro ambiente
            </Button>
            {onCancel ? (
              <Button variant="outline" onClick={onCancel} disabled={saving}>
                Voltar
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

function AccountCard({
  account,
  timeZone,
  disconnectLabel = 'Desconectar',
  environments,
}: {
  account: OnlinePaymentAccount;
  timeZone: string;
  disconnectLabel?: string;
  environments: OnlinePaymentEnvironment[];
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [mode, setMode] = useState<'key' | 'disconnect' | 'production' | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [dueDays, setDueDays] = useState(String(account.dueDays));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function run(action: () => Promise<unknown>, message: string) {
    setSaving(true);
    setError(undefined);
    try {
      await action();
      await client.invalidateQueries({ queryKey: ['company', 'online-payments'] });
      snackbar.success(message);
      setMode(null);
      setApiKey('');
    } catch (value) {
      setError(getApiError(value));
    } finally {
      setSaving(false);
    }
  }

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      snackbar.success(`${what} copiado.`);
    } catch {
      snackbar.failure(`Não deu para copiar. Selecione o ${what.toLowerCase()} e copie.`);
    }
  }

  const [checking, setChecking] = useState(false);
  const days = Number(dueDays);
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-surface-border px-3 py-2.5">
        <div className="min-w-0">
          <strong className="text-sm">{account.accountName}</strong>
          <p className="text-xs text-content-muted">
            {[
              account.accountDocumentMasked ? `CNPJ/CPF ${account.accountDocumentMasked}` : null,
              account.apiKeyLast4 ? `chave terminada em ${account.apiKeyLast4}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'Sem chave: cobrança simulada'}
          </p>
        </div>
        <StatusPill tone={account.environment === 'ASAAS_PRODUCTION' ? 'positive' : 'warning'}>
          Conectada · {ENVIRONMENT_LABELS[account.environment]}
        </StatusPill>
      </div>

      {account.noticeUrl && account.noticeToken ? (
        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">3. Configurar os avisos de pagamento</h3>
          <p className="text-xs text-content-muted">
            É assim que o Asaas conta ao sistema que a cliente pagou. Configure uma vez no painel do
            Asaas.
          </p>
          <ol className="grid list-decimal gap-2 pl-5 text-sm">
            <li>
              No Asaas, abra <strong>Integrações → Webhooks</strong> e crie um novo.
            </li>
            <li className="grid gap-1">
              Cole este endereço:
              <CopyLine
                value={account.noticeUrl}
                label="Endereço do aviso"
                onCopy={() => void copy(account.noticeUrl!, 'Endereço')}
              />
            </li>
            <li className="grid gap-1">
              Cole este token de autenticação:
              <CopyLine
                value={account.noticeToken}
                label="Token do aviso"
                onCopy={() => void copy(account.noticeToken!, 'Token')}
              />
            </li>
            <li>
              Selecione a versão v3, envio sequencial e os eventos de <strong>Cobranças</strong>.
              Mantenha o webhook e a fila de sincronização ativos e salve.
            </li>
          </ol>
          <p className="text-sm text-content-muted">
            Use o endereço completo, incluindo o caminho. Em produção, ele precisa ser HTTPS público
            e estável; localhost não recebe avisos externos.
          </p>
          <h3 className="text-sm font-semibold">4. Conferir a integração</h3>
          <p role="status" aria-live="polite" className="text-sm">
            {account.lastNoticeAt
              ? 'Aviso autenticado recebido. Confira em Pagamentos se a cobrança correspondente foi confirmada.'
              : 'Chave validada. Ainda não recebemos um aviso autenticado.'}
          </p>
          <p className="text-sm text-content-muted">
            {account.environment === 'ASAAS_SANDBOX'
              ? 'Crie uma venda de teste na Hue, gere a cobrança e simule o recebimento no sandbox do Asaas.'
              : 'Use uma cobrança de uma venda real. O assistente não cria cobranças nem movimenta dinheiro automaticamente.'}{' '}
            Esta tela acompanha os avisos a cada cinco segundos enquanto estiver aberta. Um aviso
            recebido não comprova a aprovação de um pagamento.
          </p>
          <div>
            <Button variant="outline" onClick={() => setChecking(true)}>
              Já configurei no Asaas
            </Button>
          </div>
          {checking ? (
            <p className="text-sm text-content-muted">
              Acompanhamento ativo. Você pode continuar depois e retomar nesta tela.
            </p>
          ) : null}
          <p className="text-xs text-content-muted">
            {`Último aviso recebido: ${
              account.lastNoticeAt ? formatDateTime(account.lastNoticeAt, timeZone) : 'nenhum ainda'
            }. Mesmo sem aviso, o sistema confere as cobranças abertas a cada 15 minutos.`}
          </p>
        </div>
      ) : null}

      {account.environment !== 'ASAAS_PRODUCTION' && environments.includes('ASAAS_PRODUCTION') ? (
        <div className="grid gap-3 rounded-xl border border-surface-border p-3">
          <h3 className="text-sm font-semibold">Passar para produção</h3>
          <p className="text-sm text-content-muted">
            Conecte novas credenciais e configure o novo webhook. O histórico de teste é preservado;
            cobranças anteriores continuam acompanhadas no ambiente de origem. Confira o titular
            antes de conectar.
          </p>
          {mode === 'production' ? (
            <ConnectForm
              businessUnitId={account.businessUnitId}
              environments={['ASAAS_PRODUCTION']}
              initialEnvironment="ASAAS_PRODUCTION"
              submitLabel="Conectar produção"
              onCancel={() => setMode(null)}
            />
          ) : (
            <div>
              <Button variant="outline" onClick={() => setMode('production')}>
                Conectar produção
              </Button>
            </div>
          )}
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <Input
          label="Vencimento padrão da cobrança (dias)"
          inputMode="numeric"
          value={dueDays}
          onChange={(event) => setDueDays(event.target.value.replace(/\D/gu, '').slice(0, 2))}
          className="max-w-32"
        />
        <Button
          variant="outline"
          disabled={saving || days === account.dueDays || days < 1 || days > 30}
          onClick={() =>
            void run(
              () => onlinePaymentsApi.updateDueDays(account.id, days),
              'Vencimento padrão salvo.',
            )
          }
        >
          Salvar vencimento
        </Button>
      </div>

      {mode === 'key' ? (
        <div className="grid gap-3 rounded-xl border border-brand-500/50 p-3">
          <SecretInput label="Nova chave de API" value={apiKey} onChange={setApiKey} />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={saving}
              onClick={() =>
                void run(
                  () => onlinePaymentsApi.replaceKey(account.id, apiKey.trim()),
                  'Chave trocada.',
                )
              }
            >
              Trocar chave
            </Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => setMode(null)}>
              Voltar
            </Button>
          </div>
        </div>
      ) : mode === 'disconnect' ? (
        <div className="grid gap-3 rounded-xl border border-danger/40 p-3">
          <p className="text-sm">
            Desconectar impede gerar cobrança nova com esta conta. As cobranças já geradas continuam
            sendo acompanhadas até terminar.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={saving}
              onClick={() =>
                void run(() => onlinePaymentsApi.disconnect(account.id), 'Conta desconectada.')
              }
            >
              {disconnectLabel}
            </Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => setMode(null)}>
              Voltar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {account.environment !== 'SIMULATED' ? (
            <IconButton label="Trocar chave" icon={<KeyRound />} onClick={() => setMode('key')} />
          ) : null}
          <IconButton
            label={disconnectLabel}
            icon={<Unplug />}
            tone="danger"
            onClick={() => setMode('disconnect')}
          />
        </div>
      )}
      {error ? <RequestAlert message={error} /> : null}
    </div>
  );
}

function SecretInput({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      label={label}
      type={visible ? 'text' : 'password'}
      autoComplete="off"
      spellCheck={false}
      value={value}
      hint={hint}
      onChange={(event) => onChange(event.target.value)}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Esconder a chave' : 'Mostrar a chave'}
        >
          {visible ? (
            <EyeOff className="size-4" aria-hidden="true" />
          ) : (
            <Eye className="size-4" aria-hidden="true" />
          )}
          {visible ? 'Esconder' : 'Mostrar'}
        </Button>
      }
    />
  );
}

function CopyLine({ value, label, onCopy }: { value: string; label: string; onCopy: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-lg bg-surface-elevated px-2.5 py-2 text-xs">
        {value}
      </code>
      <Button size="sm" variant="outline" onClick={onCopy} aria-label={`Copiar ${label}`}>
        <Copy className="size-4" aria-hidden="true" />
        Copiar
      </Button>
    </div>
  );
}
